import { z } from "zod";

/**
 * The API lives on another origin (this build is static, hosted on Pages),
 * which is why every call below sets `credentials: "include"` and why the API
 * had to grow a CORS allowlist. There is no token anywhere in this file on
 * purpose: the session is an httpOnly cookie the browser attaches and script
 * cannot read, so an XSS in this dashboard cannot walk off with an operator's
 * session the way a `localStorage` token would.
 */
export const API_BASE = (import.meta.env.VITE_API_URL ?? "http://localhost:8080").replace(
  /\/+$/,
  "",
);

/**
 * The same origin, as a WebSocket URL. Derived rather than configured, so a
 * deployment cannot end up pointing its live-login stream at one host and
 * its API at another -- which would look like an intermittent failure and be
 * a socket to somebody else's browser.
 */
export const WS_BASE = API_BASE.replace(/^http/, "ws");

export interface ApiIssue {
  path: string;
  message: string;
}

const ErrorBody = z.object({
  error: z.string(),
  message: z.string(),
  issues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
});

/**
 * One error type for every failure, including the one where the request never
 * arrived: a dashboard whose API is unreachable must say so, and `fetch`
 * rejecting with a bare TypeError is indistinguishable from a bug in here.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly issues: ApiIssue[] = [],
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** True when the caller is not signed in, which the session hook treats as "no user". */
  get unauthenticated(): boolean {
    return this.status === 401;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * Generic over the SCHEMA rather than over its output type: a schema with
 * defaults has a different input type to its output, and `z.ZodType<T>` makes
 * TypeScript reconcile the two by picking the input -- so `alert.filters`
 * would arrive typed with every defaulted field optional, which is precisely
 * the shape the server never sends.
 */
export async function request<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
  options: RequestOptions = {},
): Promise<z.output<S>> {
  const hasBody = options.body !== undefined;
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: options.method ?? "GET",
      credentials: "include",
      ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
      headers: hasBody ? { "content-type": "application/json" } : {},
    });
  } catch {
    // Deliberately not `error.message`: a fetch failure's text varies by
    // browser and says nothing an operator can act on.
    throw new ApiError(0, "network_error", "Could not reach the API.");
  }

  const payload: unknown =
    response.status === 204 ? null : await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = ErrorBody.safeParse(payload);
    if (parsed.success) {
      throw new ApiError(
        response.status,
        parsed.data.error,
        parsed.data.message,
        parsed.data.issues ?? [],
      );
    }
    throw new ApiError(response.status, "unexpected_error", `Request failed (${response.status}).`);
  }

  return schema.parse(payload);
}
